/**
 * useSkeletonStyles Hook
 * Creates theme-aware skeleton card styles
 * Eliminates the need for useMemo + StyleSheet.create in every skeleton component
 */

import { useMemo } from 'react';
import { StyleSheet, type ViewStyle } from 'react-native';
import { useTheme } from '../../theme/useTheme';
import {
  createSkeletonCardBase,
  type CreateSkeletonCardBaseOptions,
} from '../../../components/ui/Skeleton/skeletonCardHelpers';

interface UseSkeletonCardStylesOptions extends CreateSkeletonCardBaseOptions {
  /** Additional base style overrides */
  baseStyle?: ViewStyle;
}

interface UseSkeletonCardStylesReturn {
  card: ViewStyle;
}

/**
 * Hook that creates theme-aware skeleton card styles
 */
export const useSkeletonCardStyles = (
  options?: UseSkeletonCardStylesOptions
): UseSkeletonCardStylesReturn => {
  const theme = useTheme();

  return useMemo(() => {
    const baseCard = createSkeletonCardBase(theme, options);

    return StyleSheet.create({
      card: options?.baseStyle
        ? { ...baseCard, ...options.baseStyle }
        : baseCard,
    });
  }, [theme, options]);
};

